/**
 * <pi-drawer> + <pi-drawer-gallery>
 * Quick view for add-on "Learn More" triggers ([data-pi-drawer-open]).
 */
(() => {
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  class PiDrawerGallery extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.imageEl = this.querySelector('[data-pi-drawer-image]');
      this.paginationEl = this.querySelector('[data-pi-drawer-pagination]');
      this.countEl = this.querySelector('[data-pi-drawer-count]');
      this.prevBtn = this.querySelector('[data-pi-drawer-prev]');
      this.nextBtn = this.querySelector('[data-pi-drawer-next]');
      this.stageEl = this.querySelector('[data-pi-drawer-stage]');
      this.images = [];
      this.index = 0;

      this.prevBtn?.addEventListener('click', () => this.go(-1));
      this.nextBtn?.addEventListener('click', () => this.go(1));
      this._bindSwipe();
    }

    setImages(images) {
      this.images = Array.isArray(images) ? images.filter((item) => item && item.src) : [];
      this.index = 0;
      this.render();
    }

    go(delta) {
      if (this.images.length < 2) return;
      this.index = (this.index + delta + this.images.length) % this.images.length;
      this.render();
    }

    render() {
      const total = this.images.length;
      const current = this.images[this.index];

      if (this.imageEl) {
        if (current) {
          this.imageEl.hidden = false;
          this.imageEl.src = current.src;
          this.imageEl.alt = current.alt || '';
        } else {
          this.imageEl.hidden = true;
          this.imageEl.removeAttribute('src');
          this.imageEl.alt = '';
        }
      }

      if (this.paginationEl) {
        this.paginationEl.hidden = total < 2;
      }
      if (this.countEl) {
        this.countEl.textContent = `${this.index + 1} / ${Math.max(total, 1)}`;
      }
    }

    _bindSwipe() {
      if (!this.stageEl) return;
      let startX = 0;
      let tracking = false;

      this.stageEl.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        tracking = true;
        startX = event.clientX;
      });

      this.stageEl.addEventListener('pointerup', (event) => {
        if (!tracking) return;
        tracking = false;
        const delta = event.clientX - startX;
        if (Math.abs(delta) < 40) return;
        this.go(delta < 0 ? 1 : -1);
      });

      this.stageEl.addEventListener('pointercancel', () => {
        tracking = false;
      });
    }
  }

  class PiDrawer extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.overlay = this.querySelector('[data-pi-drawer-overlay]');
      this.panel = this.querySelector('.pi-drawer__panel');
      this.closeBtn = this.querySelector('[data-pi-drawer-close]');
      this.titleEl = this.querySelector('[data-pi-drawer-title]');
      this.compareEl = this.querySelector('[data-pi-drawer-compare]');
      this.priceEl = this.querySelector('[data-pi-drawer-price]');
      this.descriptionEl = this.querySelector('[data-pi-drawer-description]');
      this.gallery = this.querySelector('pi-drawer-gallery');
      this._trigger = null;

      this._onTriggerClick = this._onTriggerClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onFocusIn = this._onFocusIn.bind(this);

      this.overlay?.addEventListener('click', () => this.close());
      this.closeBtn?.addEventListener('click', () => this.close());

      this._onOpenEvent = this._onOpenEvent.bind(this);
      document.addEventListener('click', this._onTriggerClick, true);
      document.addEventListener('pointerdown', this._onTriggerClick, true);
      document.addEventListener('pi-drawer:open', this._onOpenEvent);

      this._sectionId = this.closest('.shopify-section')?.id || '';
      this._onSectionUnload = this._onSectionUnload.bind(this);
      document.addEventListener('shopify:section:unload', this._onSectionUnload);

      document.querySelectorAll('pi-drawer').forEach((el) => {
        if (el !== this) el.remove();
      });

      if (this.parentElement !== document.body) {
        this._movingToBody = true;
        document.body.append(this);
        this._movingToBody = false;
      }
    }

    disconnectedCallback() {
      if (this._movingToBody) return;
      document.removeEventListener('click', this._onTriggerClick, true);
      document.removeEventListener('pointerdown', this._onTriggerClick, true);
      document.removeEventListener('keydown', this._onKeydown, true);
      document.removeEventListener('shopify:section:unload', this._onSectionUnload);
      document.removeEventListener('pi-drawer:open', this._onOpenEvent);
      this.removeEventListener('focusin', this._onFocusIn);
      if (this.open) this._unlock();
    }

    _onSectionUnload(event) {
      if (this._sectionId && event.target?.id === this._sectionId) {
        this.remove();
      }
    }

    get open() {
      return this.hasAttribute('open');
    }

    _onOpenEvent(event) {
      const detail = event.detail || {};
      this.show(detail.payload || {}, detail.trigger || null);
    }

    _onTriggerClick(event) {
      if (event.type === 'pointerdown' && event.pointerType === 'mouse' && event.button !== 0) return;

      const trigger = event.target?.closest?.('[data-pi-drawer-open]');
      if (!trigger) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      let payload = {};
      const raw = trigger.getAttribute('data-pi-drawer-payload');
      if (raw) {
        try {
          payload = JSON.parse(raw);
        } catch (error) {
          console.error('[pi-drawer] invalid product payload', error);
        }
      }

      this.show(payload, trigger);
    }

    show(payload = {}, trigger = null) {
      if (this._trigger && this._trigger !== trigger) {
        this._trigger.setAttribute('aria-expanded', 'false');
      }
      this._trigger = trigger;
      if (this._trigger) this._trigger.setAttribute('aria-expanded', 'true');
      this._fill(payload);
      this._cancelClose();
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      this.classList.remove('is-closing');
      this.classList.add('is-open');
      this._lock();
      document.addEventListener('keydown', this._onKeydown, true);
      this.addEventListener('focusin', this._onFocusIn);

      requestAnimationFrame(() => {
        this.panel?.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.open && !this.classList.contains('is-open')) return;
      if (this.classList.contains('is-closing')) return;

      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      this.classList.remove('is-open');
      this.classList.add('is-closing');
      this._unlock();
      document.removeEventListener('keydown', this._onKeydown, true);
      this.removeEventListener('focusin', this._onFocusIn);

      const trigger = this._trigger;
      this._trigger = null;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');

      const finish = () => {
        this.classList.remove('is-closing');
        this._closeTimer = null;
      };

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion || !this.panel) {
        finish();
        return;
      }

      this._onCloseEnd = (event) => {
        if (event.target !== this.panel || event.propertyName !== 'transform') return;
        this.panel.removeEventListener('transitionend', this._onCloseEnd);
        finish();
      };
      this.panel.addEventListener('transitionend', this._onCloseEnd);
      this._closeTimer = window.setTimeout(() => {
        this.panel.removeEventListener('transitionend', this._onCloseEnd);
        finish();
      }, 450);
    }

    _cancelClose() {
      if (this._onCloseEnd && this.panel) {
        this.panel.removeEventListener('transitionend', this._onCloseEnd);
      }
      if (this._closeTimer) {
        window.clearTimeout(this._closeTimer);
        this._closeTimer = null;
      }
    }

    _fill(payload) {
      if (this.titleEl) this.titleEl.textContent = payload.title || '';
      if (this.priceEl) this.priceEl.textContent = payload.price || '';
      if (this.compareEl) this.compareEl.textContent = payload.compare || '';
      if (this.descriptionEl) this.descriptionEl.textContent = payload.description || '';
      if (this.gallery && typeof this.gallery.setImages === 'function') {
        this.gallery.setImages(payload.images || []);
      }
    }

    _lock() {
      if (this._scrollLocked) return;
      this._scrollLocked = true;
      document.documentElement.classList.add('pi-drawer-lock');
      this._onScrollLock = this._onScrollLock.bind(this);
      document.addEventListener('wheel', this._onScrollLock, { capture: true, passive: false });
      document.addEventListener('touchmove', this._onScrollLock, { capture: true, passive: false });
    }

    _unlock() {
      if (!this._scrollLocked) return;
      this._scrollLocked = false;
      document.documentElement.classList.remove('pi-drawer-lock');
      document.removeEventListener('wheel', this._onScrollLock, { capture: true });
      document.removeEventListener('touchmove', this._onScrollLock, { capture: true });
    }

    _onScrollLock(event) {
      if (this.panel && this.panel.contains(event.target)) return;
      event.preventDefault();
    }

    _onKeydown(event) {
      if (!this.open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.gallery?.go(-1);
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.gallery?.go(1);
        return;
      }
      if (event.key !== 'Tab') return;

      const nodes = this._focusable();
      if (!nodes.length) {
        event.preventDefault();
        this.panel?.focus();
        return;
      }

      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !this.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !this.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    _onFocusIn(event) {
      if (!this.open) return;
      if (this.contains(event.target)) return;
      const nodes = this._focusable();
      (nodes[0] || this.panel)?.focus();
    }

    _focusable() {
      return Array.from(this.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null && !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true'
      );
    }
  }

  if (!customElements.get('pi-drawer-gallery')) {
    customElements.define('pi-drawer-gallery', PiDrawerGallery);
  }
  if (!customElements.get('pi-drawer')) {
    customElements.define('pi-drawer', PiDrawer);
  }
})();
